/**
 * Configuration system for ledger entry editor field-level control.
 * Supports granular field inclusion per mutation and presentation mode.
 */

/**
 * Ledger entry core fields (entry-level mutations).
 */
export type EntryFieldKey =
  | 'amount'
  | 'code'
  | 'description'
  | 'kind'
  | 'entryDate'
  | 'letter'
  | 'number'
  | 'months'
  | 'months_advanced'
  | 'annuality';

/**
 * Party association fields (association-level mutations).
 */
export type AssociationFieldKey =
  | 'associationPercent'
  | 'associationAmount'
  | 'associationKind';

/**
 * Party/involved-party fields.
 */
export type PartyFieldKey =
  | 'partyName'
  | 'partyEmail'
  | 'partyKind';

/**
 * Linked entry fields.
 */
export type LinkFieldKey =
  | 'linkedEntryId'
  | 'linkedAmount';

/**
 * Settings/global toggles.
 */
export type SettingsFieldKey =
  | 'posted'
  | 'faked'
  | 'overridden';

export type FieldKey =
  | EntryFieldKey
  | AssociationFieldKey
  | PartyFieldKey
  | LinkFieldKey
  | SettingsFieldKey;

/**
 * Field-level configuration for editor capabilities.
 * Each field can be included/excluded independently, enabling single-field presentation.
 */
export interface LedgerEditorConfig {
  // Entry fields
  entryFields?: EntryFieldKey[];

  // Association fields
  associationFields?: AssociationFieldKey[];

  // Party fields
  partyFields?: PartyFieldKey[];

  // Link fields
  linkFields?: LinkFieldKey[];

  // Settings fields
  settingsFields?: SettingsFieldKey[];

  // Mutations to enable (e.g., 'createEntry', 'updateEntry', 'deleteEntry')
  enabledMutations?: string[];

  // Toggle settings
  enableDelete?: boolean;
  enablePosted?: boolean;
  enableFaked?: boolean;
  enableOverridden?: boolean;

  // Validation rules
  requireDescription?: boolean;
  requireAmount?: boolean;
  requireKind?: boolean;
  requireCode?: boolean;
}

/**
 * Tab configuration derived from field selection.
 * Empty tabs are hidden automatically.
 */
export interface TabConfig {
  id: string;
  label: string;
  fields: FieldKey[];
}

/**
 * Derive visible tabs from configuration.
 * SCOPE REDUCTION: Only Entry tab is exposed. Association, Party, and Link tabs
 * are no longer rendered, even if their config fields are present.
 * This is a deliberate scope reduction; future reintroduction would require
 * re-enabling the tab derivation logic below.
 *
 * Hides tabs with no selected fields.
 * Defensively filters out internal metadata fields (faked, overridden).
 * Excludes settings tab when only user-facing option is Posted
 * (Posted is rendered inline next to Amount instead).
 */
export function deriveVisibleTabs(config: LedgerEditorConfig): TabConfig[] {
  const tabs: TabConfig[] = [];

  // Entry tab — ONLY tab exposed in current scope
  if (config.entryFields && config.entryFields.length > 0) {
    tabs.push({
      id: 'entry',
      label: 'entry',
      fields: config.entryFields,
    });
  }

  // Association, Party, and Link tabs are intentionally NOT rendered.
  // These field groups are preserved in LedgerEditorConfig for future use,
  // but deriveVisibleTabs() ignores them per scope reduction.
  // To re-enable: uncomment the blocks below and update LedgerTestScreen.
  //
  // // Association tab
  // if (config.associationFields && config.associationFields.length > 0) {
  //   tabs.push({
  //     id: 'association',
  //     label: 'association',
  //     fields: config.associationFields,
  //   });
  // }
  //
  // // Party tab
  // if (config.partyFields && config.partyFields.length > 0) {
  //   tabs.push({
  //     id: 'party',
  //     label: 'party',
  //     fields: config.partyFields,
  //   });
  // }
  //
  // // Link tab
  // if (config.linkFields && config.linkFields.length > 0) {
  //   tabs.push({
  //     id: 'link',
  //     label: 'link',
  //     fields: config.linkFields,
  //   });
  // }

  // Settings tab is hidden: Posted is rendered inline next to Amount field instead
  // Defensive filter: ensure only safe settings reach any future caller
  // (though settings tab is now excluded from tabs list)

  return tabs;
}

/**
 * Check if this is a single-field presentation.
 */
export function isSingleFieldMode(config: LedgerEditorConfig): boolean {
  const totalFields =
    (config.entryFields?.length || 0) +
    (config.associationFields?.length || 0) +
    (config.partyFields?.length || 0) +
    (config.linkFields?.length || 0) +
    (config.settingsFields?.length || 0);

  return totalFields === 1;
}

/**
 * Get the single field in single-field mode.
 */
export function getSingleField(config: LedgerEditorConfig): FieldKey | null {
  if (!isSingleFieldMode(config)) return null;

  if (config.entryFields && config.entryFields.length === 1) {
    return config.entryFields[0];
  }
  if (config.associationFields && config.associationFields.length === 1) {
    return config.associationFields[0];
  }
  if (config.partyFields && config.partyFields.length === 1) {
    return config.partyFields[0];
  }
  if (config.linkFields && config.linkFields.length === 1) {
    return config.linkFields[0];
  }
  if (config.settingsFields && config.settingsFields.length === 1) {
    return config.settingsFields[0];
  }

  return null;
}

/**
 * Validate field inclusion in config.
 */
export function isFieldIncluded(config: LedgerEditorConfig, fieldKey: FieldKey): boolean {
  if (config.entryFields?.includes(fieldKey as EntryFieldKey)) return true;
  if (config.associationFields?.includes(fieldKey as AssociationFieldKey)) return true;
  if (config.partyFields?.includes(fieldKey as PartyFieldKey)) return true;
  if (config.linkFields?.includes(fieldKey as LinkFieldKey)) return true;
  if (config.settingsFields?.includes(fieldKey as SettingsFieldKey)) return true;
  return false;
}
